import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Key, Shield, Bell, MessageSquare, ExternalLink, Save, Loader2,
  CheckCircle2, AlertCircle, Info, Lock, Slack, Users, Bug, Github,
  FolderGit2, TicketCheck, RefreshCw, TestTube, Copy, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/services/api';

// All requests go through the shared axios client from ``services/api.js``.
// That client's request interceptor attaches ``Authorization`` + the
// tenant-scoped ``X-Tenant-ID`` header so integrations save/read against the
// tenant the owner currently has selected. Previously this component used
// raw ``axios`` which meant Jira/GitHub/Slack config saves landed in the
// wrong tenant bucket (or failed outright for owners without a global-admin
// role).

// Microsoft Icon
const MicrosoftIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 21 21">
    <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
    <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
    <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
  </svg>
);

// Okta Icon
const OktaIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#007dc1">
    <path d="M12 0C5.389 0 0 5.389 0 12s5.389 12 12 12 12-5.389 12-12S18.611 0 12 0zm0 18c-3.314 0-6-2.686-6-6s2.686-6 6-6 6 2.686 6 6-2.686 6-6 6z"/>
  </svg>
);

// Teams Icon
const TeamsIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#6264a7">
    <path d="M20.625 8.073c-.046-.142-.137-.266-.263-.355l-.011-.008a1.018 1.018 0 00-.592-.185h-2.384V6.16c0-.559-.453-1.012-1.012-1.012h-2.25V3.375c0-.621-.504-1.125-1.125-1.125h-2.25a1.125 1.125 0 00-1.125 1.125v1.773h-2.25c-.559 0-1.012.453-1.012 1.012v1.365H3.967c-.224 0-.44.073-.617.21l-.011.008a.947.947 0 00-.339.723v6.534c0 .31.15.601.403.779l.011.008c.15.103.328.163.514.177l.039.002h2.384v2.409c0 .559.453 1.012 1.012 1.012h9c.559 0 1.012-.453 1.012-1.012V15.966h2.384c.186-.012.364-.073.515-.177l.011-.008a.947.947 0 00.403-.779V8.468c0-.142-.039-.281-.112-.402z"/>
  </svg>
);

const IntegrationsSettings = () => {
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [testingConnection, setTestingConnection] = useState({});
  
  // SSO Configuration
  const [ssoConfig, setSsoConfig] = useState({
    microsoft: {
      enabled: false,
      client_id: '',
      client_secret: '',
      tenant_id: ''
    },
    okta: {
      enabled: false,
      domain: '',
      client_id: '',
      client_secret: ''
    },
    google: {
      enabled: true, // Google is pre-configured via Emergent
      configured: true
    }
  });

  // Notification Configuration
  const [notificationConfig, setNotificationConfig] = useState({
    slack: {
      enabled: false,
      webhook_url: '',
      channel: '#security-alerts'
    },
    teams: {
      enabled: false,
      webhook_url: ''
    }
  });

  // LLM Configuration
  const [llmConfig, setLlmConfig] = useState({
    provider: 'openai',
    model: 'gpt-4o-mini',
    has_key: false
  });
  
  // Scanner Integrations - Jira
  const [jiraConfig, setJiraConfig] = useState({
    enabled: false,
    email: '',
    api_token: '',
    domain: '',
    default_project_key: ''
  });
  const [jiraStatus, setJiraStatus] = useState(null);
  const [jiraProjects, setJiraProjects] = useState([]);
  const [jiraOauth, setJiraOauth] = useState(null); // {connected, site_name, site_url, cloud_id, ...}
  const [jiraOauthBusy, setJiraOauthBusy] = useState(false);
  
  // Scanner Integrations - GitHub
  const [githubConfig, setGithubConfig] = useState({
    enabled: false,
    token: '',
    enterprise_url: 'https://api.github.com'
  });
  const [githubStatus, setGithubStatus] = useState(null);

  useEffect(() => {
    loadConfig();
    loadScannerIntegrations();
    loadJiraOauthStatus();
    // If Atlassian bounced us back with ?jira=connected, refresh + toast
    const q = new URLSearchParams(window.location.search);
    if (q.get('jira') === 'connected') {
      toast.success('Jira Cloud connected via Atlassian OAuth');
      // Strip the query param so a page reload doesn't re-fire the toast
      const clean = window.location.pathname + (q.get('tab') ? `?tab=${q.get('tab')}` : '');
      window.history.replaceState({}, '', clean);
    }
  }, []);

  const loadJiraOauthStatus = async () => {
    try {
      const res = await api.get('/integrations/jira/oauth/status');
      setJiraOauth(res.data);
    } catch (e) {
      // Silent — OAuth may simply not be configured yet
      setJiraOauth({ connected: false });
    }
  };

  const startJiraOauth = async () => {
    setJiraOauthBusy(true);
    try {
      const res = await api.get('/integrations/jira/oauth/authorize', {
        params: { return_to: window.location.pathname + window.location.search },
      });
      if (res.data?.authorize_url) {
        window.location.assign(res.data.authorize_url);
      } else {
        toast.error('Could not start Atlassian OAuth');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start Atlassian OAuth');
    } finally {
      setJiraOauthBusy(false);
    }
  };

  const disconnectJiraOauth = async () => {
    if (!window.confirm('Disconnect Jira Cloud OAuth? Existing tickets are unaffected.')) return;
    setJiraOauthBusy(true);
    try {
      await api.post('/integrations/jira/oauth/disconnect');
      toast.success('Jira Cloud disconnected');
      setJiraOauth({ connected: false });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to disconnect');
    } finally {
      setJiraOauthBusy(false);
    }
  };

  const loadConfig = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Load integrations config
      const response = await api.get(`/settings/integrations`, { headers });
      if (response.data) {
        // Merge SSO config carefully to preserve defaults
        if (response.data.sso) {
          setSsoConfig(prev => ({
            microsoft: { ...prev.microsoft, ...(response.data.sso.microsoft || {}) },
            okta: { ...prev.okta, ...(response.data.sso.okta || {}) },
            google: { ...prev.google, ...(response.data.sso.google || {}) }
          }));
        }
        // Merge notification config carefully
        if (response.data.notifications) {
          setNotificationConfig(prev => ({
            slack: { ...prev.slack, ...(response.data.notifications.slack || {}) },
            teams: { ...prev.teams, ...(response.data.notifications.teams || {}) }
          }));
        }
        if (response.data.llm) setLlmConfig(response.data.llm);
        
        // Load saved scanner integration configs
        if (response.data.jira) {
          setJiraConfig(prev => ({ ...prev, ...response.data.jira }));
        }
        if (response.data.github) {
          setGithubConfig(prev => ({ ...prev, ...response.data.github }));
        }
      }
    } catch (error) {
      console.log('No saved integrations config');
    } finally {
      setLoading(false);
    }
  };
  
  const loadScannerIntegrations = async () => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      // Load Jira status
      const jiraRes = await api.get(`/integrations/jira/status`, { headers });
      setJiraStatus(jiraRes.data);
      
      // If connected, load projects
      if (jiraRes.data.connected) {
        try {
          const projectsRes = await api.get(`/integrations/jira/projects`, { headers });
          setJiraProjects(projectsRes.data);
        } catch (e) {
          console.log('Could not load Jira projects');
        }
      }
    } catch (error) {
      console.log('Jira not configured');
    }
    
    try {
      // Load GitHub status
      const githubRes = await api.get(`/integrations/github/status`, { headers });
      setGithubStatus(githubRes.data);
    } catch (error) {
      console.log('GitHub not configured');
    }
  };

  const handleSaveSSO = async (provider) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await api.post(`/settings/sso/${provider}`,
        ssoConfig[provider],
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${provider.charAt(0).toUpperCase() + provider.slice(1)} SSO configuration saved`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save SSO configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNotifications = async (provider) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await api.post(`/settings/notifications/${provider}`,
        notificationConfig[provider],
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${provider.charAt(0).toUpperCase() + provider.slice(1)} notifications configured`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save notification configuration');
    } finally {
      setSaving(false);
    }
  };

  const testNotification = async (provider) => {
    try {
      const token = localStorage.getItem('token');
      await api.post(`/settings/notifications/${provider}/test`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Test notification sent to ${provider}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test notification');
    }
  };
  
  // Jira Integration Handlers
  const handleSaveJira = async () => {
    setSaving(true);
    setTestingConnection(prev => ({ ...prev, jira: true }));
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
      
      // First test connection via configure endpoint
      const response = await api.post(`/integrations/jira/configure`,
        {
          email: jiraConfig.email,
          api_token: jiraConfig.api_token,
          domain: jiraConfig.domain,
          default_project_key: jiraConfig.default_project_key
        },
        { headers }
      );
      
      // Save config to settings
      await api.post(`/settings/integrations`,
        {
          jira: {
            enabled: true,
            email: jiraConfig.email,
            api_token: jiraConfig.api_token,
            domain: jiraConfig.domain,
            default_project_key: jiraConfig.default_project_key
          }
        },
        { headers }
      );
      
      toast.success(`Jira configured successfully for ${response.data.user}`);
      setJiraConfig(prev => ({ ...prev, enabled: true }));
      loadScannerIntegrations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to configure Jira');
    } finally {
      setSaving(false);
      setTestingConnection(prev => ({ ...prev, jira: false }));
    }
  };
  
  const handleTestJira = async () => {
    setTestingConnection(prev => ({ ...prev, jira: true }));
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
      
      const response = await api.post(`/integrations/jira/configure`,
        {
          email: jiraConfig.email,
          api_token: jiraConfig.api_token,
          domain: jiraConfig.domain
        },
        { headers }
      );
      
      toast.success(`Connection successful! Authenticated as ${response.data.user}`);
      loadScannerIntegrations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Connection test failed');
    } finally {
      setTestingConnection(prev => ({ ...prev, jira: false }));
    }
  };
  
  // GitHub Integration Handlers
  const handleSaveGitHub = async () => {
    setSaving(true);
    setTestingConnection(prev => ({ ...prev, github: true }));
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
      
      // First test connection via configure endpoint
      const response = await api.post(`/integrations/github/configure`,
        {
          token: githubConfig.token,
          enterprise_url: githubConfig.enterprise_url || 'https://api.github.com'
        },
        { headers }
      );
      
      // Save config to settings
      await api.post(`/settings/integrations`,
        {
          github: {
            enabled: true,
            token: githubConfig.token,
            enterprise_url: githubConfig.enterprise_url
          }
        },
        { headers }
      );
      
      toast.success(`GitHub configured successfully for @${response.data.user}`);
      setGithubConfig(prev => ({ ...prev, enabled: true }));
      loadScannerIntegrations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to configure GitHub');
    } finally {
      setSaving(false);
      setTestingConnection(prev => ({ ...prev, github: false }));
    }
  };
  
  const handleTestGitHub = async () => {
    setTestingConnection(prev => ({ ...prev, github: true }));
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
      
      const response = await api.post(`/integrations/github/configure`,
        {
          token: githubConfig.token,
          enterprise_url: githubConfig.enterprise_url || 'https://api.github.com'
        },
        { headers }
      );
      
      toast.success(`Connection successful! Authenticated as @${response.data.user}`);
      loadScannerIntegrations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Connection test failed');
    } finally {
      setTestingConnection(prev => ({ ...prev, github: false }));
    }
  };
  
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Tabs defaultValue="scanner">
        <TabsList>
          <TabsTrigger value="scanner" className="gap-2">
            <Bug className="w-4 h-4" />
            Scanner
          </TabsTrigger>
          <TabsTrigger value="sso" className="gap-2">
            <Shield className="w-4 h-4" />
            Single Sign-On
          </TabsTrigger>
          <TabsTrigger value="notifications" className="gap-2">
            <Bell className="w-4 h-4" />
            Notifications
          </TabsTrigger>
          <TabsTrigger value="llm" className="gap-2">
            <Key className="w-4 h-4" />
            AI/LLM Keys
          </TabsTrigger>
        </TabsList>
        
        {/* Scanner Integrations - Jira & GitHub */}
        <TabsContent value="scanner" className="mt-6 space-y-6">
          {/* Jira Integration Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                  <TicketCheck className="w-6 h-6 text-white" />
                </div>
                <div>
                  <span>Jira Cloud Integration</span>
                  {jiraStatus?.connected ? (
                    <Badge className="bg-green-100 text-green-700 ml-3">Connected</Badge>
                  ) : (
                    <Badge variant="outline" className="ml-3">Not configured</Badge>
                  )}
                </div>
              </CardTitle>
              <CardDescription>
                Automatically create Jira tickets from security findings with severity mapping and remediation steps.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Status Display */}
              {jiraStatus?.connected && (
                <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <span className="text-sm text-green-800">
                    Connected as <strong>{jiraStatus.details?.user}</strong> ({jiraStatus.details?.email})
                  </span>
                </div>
              )}
              
              {/* OAuth 3LO (recommended when Jira Cloud enforces SSO for API access) */}
              <div
                className="border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 to-blue-50 rounded-lg p-4"
                data-testid="jira-oauth-card"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-semibold text-indigo-900">
                        Atlassian OAuth 2.0 (recommended)
                      </span>
                      {jiraOauth?.connected ? (
                        <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">
                          Connected
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="border-indigo-300 text-indigo-700">
                          Not connected
                        </Badge>
                      )}
                    </div>
                    {jiraOauth?.connected ? (
                      <p className="text-xs text-slate-700">
                        Connected to{' '}
                        <strong data-testid="jira-oauth-site-name">
                          {jiraOauth.site_name || jiraOauth.site_url}
                        </strong>{' '}
                        (cloud id <code className="text-[10px]">{jiraOauth.cloud_id?.slice(0, 8)}…</code>).
                        Tokens auto-refresh, works with SSO-enforced sites.
                      </p>
                    ) : (
                      <p className="text-xs text-slate-700">
                        Works when your Jira Cloud site enforces SSO for API access.
                        Sign in on Atlassian&apos;s page and grant Beskar access to your projects.
                        No API tokens to manage.
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 items-end">
                    {jiraOauth?.connected ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={disconnectJiraOauth}
                        disabled={jiraOauthBusy}
                        data-testid="jira-oauth-disconnect-btn"
                      >
                        Disconnect
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        className="bg-indigo-600 hover:bg-indigo-700 text-white"
                        onClick={startJiraOauth}
                        disabled={jiraOauthBusy}
                        data-testid="jira-oauth-connect-btn"
                      >
                        {jiraOauthBusy ? 'Redirecting…' : 'Connect with Atlassian'}
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* Setup Guide */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
                  <div className="text-sm text-blue-800">
                    <p className="font-semibold mb-2">Or use a classic API Token (skip if you connected OAuth above):</p>
                    <ol className="list-decimal list-inside space-y-2 text-blue-700">
                      <li>Go to <a href="https://id.atlassian.com/manage-profile/security/api-tokens" target="_blank" rel="noopener noreferrer" className="text-blue-600 font-medium underline hover:text-blue-800">Atlassian API Tokens</a> <ExternalLink className="w-3 h-3 inline" /></li>
                      <li>Click <strong>"Create API token"</strong></li>
                      <li>Enter a label (e.g., "Beskar GRC Scanner")</li>
                      <li>Click <strong>"Create"</strong> and copy the token</li>
                      <li>Your <strong>Jira Domain</strong> is: <code className="bg-blue-100 px-1.5 py-0.5 rounded text-xs">your-company.atlassian.net</code></li>
                    </ol>
                  </div>
                </div>
              </div>
              
              {/* Configuration Form */}
              <div className="grid gap-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="jira-email">Atlassian Account Email *</Label>
                    <Input
                      id="jira-email"
                      placeholder="admin@company.com"
                      value={jiraConfig.email}
                      onChange={(e) => setJiraConfig(prev => ({ ...prev, email: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="jira-domain">Jira Domain *</Label>
                    <Input
                      id="jira-domain"
                      placeholder="your-company.atlassian.net"
                      value={jiraConfig.domain}
                      onChange={(e) => setJiraConfig(prev => ({ ...prev, domain: e.target.value }))}
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="jira-token">API Token *</Label>
                  <Input
                    id="jira-token"
                    type="password"
                    placeholder="Enter your Jira API token"
                    value={jiraConfig.api_token}
                    onChange={(e) => setJiraConfig(prev => ({ ...prev, api_token: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="jira-project">Default Project Key (Optional)</Label>
                  <Input
                    id="jira-project"
                    placeholder="SEC (tickets will be created in this project by default)"
                    value={jiraConfig.default_project_key}
                    onChange={(e) => setJiraConfig(prev => ({ ...prev, default_project_key: e.target.value }))}
                  />
                  {jiraProjects.length > 0 && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Available projects: {jiraProjects.slice(0, 5).map(p => p.key).join(', ')}
                      {jiraProjects.length > 5 && ` +${jiraProjects.length - 5} more`}
                    </p>
                  )}
                </div>
              </div>
              
              {/* Actions */}
              <div className="flex gap-3">
                <Button 
                  onClick={handleSaveJira} 
                  disabled={saving || !jiraConfig.email || !jiraConfig.api_token || !jiraConfig.domain}
                >
                  {saving && testingConnection.jira ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save & Connect
                </Button>
                <Button 
                  variant="outline"
                  onClick={handleTestJira}
                  disabled={testingConnection.jira || !jiraConfig.email || !jiraConfig.api_token || !jiraConfig.domain}
                >
                  {testingConnection.jira ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <TestTube className="w-4 h-4 mr-2" />
                  )}
                  Test Connection
                </Button>
              </div>
              
              {/* Features */}
              <div className="border-t pt-4 mt-4">
                <p className="text-sm font-medium mb-2">What you can do with Jira Integration:</p>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li className="flex items-center gap-2"><ChevronRight className="w-4 h-4" /> Create tickets from critical/high findings with one click</li>
                  <li className="flex items-center gap-2"><ChevronRight className="w-4 h-4" /> Auto-map severity to Jira priority (Critical → Highest)</li>
                  <li className="flex items-center gap-2"><ChevronRight className="w-4 h-4" /> Include CVSS scores, remediation steps, and code examples</li>
                  <li className="flex items-center gap-2"><ChevronRight className="w-4 h-4" /> Link findings to compliance frameworks (OWASP, CWE)</li>
                </ul>
              </div>
            </CardContent>
          </Card>
          
          {/* GitHub Integration Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gray-900 rounded-lg flex items-center justify-center">
                  <Github className="w-6 h-6 text-white" />
                </div>
                <div>
                  <span>GitHub Integration</span>
                  {githubStatus?.connected ? (
                    <Badge className="bg-green-100 text-green-700 ml-3">Connected</Badge>
                  ) : (
                    <Badge variant="outline" className="ml-3">Not configured</Badge>
                  )}
                </div>
              </CardTitle>
              <CardDescription>
                Scan private repositories for security vulnerabilities, hardcoded secrets, and misconfigurations.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Status Display */}
              {githubStatus?.connected && (
                <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <span className="text-sm text-green-800">
                    Connected as <strong>@{githubStatus.details?.user}</strong>
                    {githubStatus.details?.scopes?.length > 0 && (
                      <span className="text-green-600 ml-2">
                        (scopes: {githubStatus.details.scopes.join(', ')})
                      </span>
                    )}
                  </span>
                </div>
              )}
              
              {/* Setup Guide */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-gray-600 mt-0.5 shrink-0" />
                  <div className="text-sm text-gray-700">
                    <p className="font-semibold mb-2">How to create a GitHub Personal Access Token:</p>
                    <ol className="list-decimal list-inside space-y-2">
                      <li>Go to <a href="https://github.com/settings/tokens/new?description=Beskar%20GRC%20Scanner&scopes=repo" target="_blank" rel="noopener noreferrer" className="text-blue-600 font-medium underline hover:text-blue-800">GitHub Token Settings</a> <ExternalLink className="w-3 h-3 inline" /></li>
                      <li>Set expiration (recommended: 90 days or custom)</li>
                      <li>Select scopes:
                        <ul className="list-disc list-inside ml-4 mt-1 text-gray-600">
                          <li><code className="bg-gray-200 px-1 rounded text-xs">repo</code> - Full control of private repositories</li>
                          <li><code className="bg-gray-200 px-1 rounded text-xs">read:org</code> - (Optional) Read org repositories</li>
                        </ul>
                      </li>
                      <li>Click <strong>"Generate token"</strong> and copy it immediately</li>
                    </ol>
                    <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded">
                      <p className="text-xs text-yellow-800 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        Token is only shown once! Store it securely.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Configuration Form */}
              <div className="grid gap-4">
                <div>
                  <Label htmlFor="github-token">Personal Access Token *</Label>
                  <Input
                    id="github-token"
                    type="password"
                    placeholder="ghp_xxxxxxxxxxxxxxxxxxxx"
                    value={githubConfig.token}
                    onChange={(e) => setGithubConfig(prev => ({ ...prev, token: e.target.value }))}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Starts with <code className="bg-gray-100 px-1 rounded">ghp_</code> for classic tokens or <code className="bg-gray-100 px-1 rounded">github_pat_</code> for fine-grained
                  </p>
                </div>
                <div>
                  <Label htmlFor="github-enterprise">Enterprise URL (Optional)</Label>
                  <Input
                    id="github-enterprise"
                    placeholder="https://api.github.com (default for GitHub.com)"
                    value={githubConfig.enterprise_url}
                    onChange={(e) => setGithubConfig(prev => ({ ...prev, enterprise_url: e.target.value }))}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    For GitHub Enterprise: <code className="bg-gray-100 px-1 rounded">https://github.yourcompany.com/api/v3</code>
                  </p>
                </div>
              </div>
              
              {/* Actions */}
              <div className="flex gap-3">
                <Button 
                  onClick={handleSaveGitHub} 
                  disabled={saving || !githubConfig.token}
                >
                  {saving && testingConnection.github ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save & Connect
                </Button>
                <Button 
                  variant="outline"
                  onClick={handleTestGitHub}
                  disabled={testingConnection.github || !githubConfig.token}
                >
                  {testingConnection.github ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <TestTube className="w-4 h-4 mr-2" />
                  )}
                  Test Connection
                </Button>
              </div>
              
              {/* Features */}
              <div className="border-t pt-4 mt-4">
                <p className="text-sm font-medium mb-2">What you can do with GitHub Integration:</p>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li className="flex items-center gap-2"><ChevronRight className="w-4 h-4" /> Scan private repositories for security issues</li>
                  <li className="flex items-center gap-2"><ChevronRight className="w-4 h-4" /> Detect hardcoded credentials and API keys</li>
                  <li className="flex items-center gap-2"><ChevronRight className="w-4 h-4" /> Find SQL injection, command injection patterns</li>
                  <li className="flex items-center gap-2"><ChevronRight className="w-4 h-4" /> Identify weak cryptography and insecure configs</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SSO Configuration */}
        <TabsContent value="sso" className="mt-6 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5" />
                Single Sign-On (SSO) Configuration
              </CardTitle>
              <CardDescription>
                Configure identity providers for secure authentication. Super Admin only.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-2">
                  <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium">Setup Instructions</p>
                    <p className="mt-1">
                      SSO requires configuration in your identity provider's admin console. 
                      After setting up, enter the credentials below to enable SSO login.
                    </p>
                  </div>
                </div>
              </div>

              <Accordion type="single" collapsible className="space-y-4">
                {/* Google SSO */}
                <AccordionItem value="google" className="border rounded-lg px-4">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                      <span className="font-medium">Google Workspace</span>
                      <Badge className="bg-green-100 text-green-700 ml-2">Pre-configured</Badge>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pt-4">
                    <p className="text-sm text-muted-foreground mb-4">
                      Google SSO is pre-configured via Emergent Auth. No additional setup required.
                    </p>
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                      <span className="text-sm text-green-600">Active and ready to use</span>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Microsoft SSO */}
                <AccordionItem value="microsoft" className="border rounded-lg px-4">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3">
                      <MicrosoftIcon />
                      <span className="font-medium">Microsoft Azure AD</span>
                      {ssoConfig.microsoft.enabled ? (
                        <Badge className="bg-green-100 text-green-700 ml-2">Configured</Badge>
                      ) : (
                        <Badge variant="outline" className="ml-2">Not configured</Badge>
                      )}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pt-4 space-y-4">
                    <div className="bg-slate-50 rounded-lg p-4 text-sm">
                      <p className="font-medium mb-2">Setup Steps:</p>
                      <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                        <li>Go to <a href="https://portal.azure.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Azure Portal</a></li>
                        <li>Navigate to Azure Active Directory → App registrations</li>
                        <li>Create a new registration with redirect URI: <code className="bg-slate-200 px-1 rounded">{window.location.origin}/api/auth/sso/microsoft/callback</code></li>
                        <li>Copy the Application (client) ID and Directory (tenant) ID</li>
                        <li>Create a client secret under Certificates & secrets</li>
                      </ol>
                    </div>

                    <div className="flex items-center justify-between">
                      <Label>Enable Microsoft SSO</Label>
                      <Switch
                        checked={ssoConfig.microsoft.enabled}
                        onCheckedChange={(checked) => setSsoConfig(prev => ({
                          ...prev,
                          microsoft: { ...prev.microsoft, enabled: checked }
                        }))}
                      />
                    </div>

                    <div className="grid gap-4">
                      <div>
                        <Label>Client ID</Label>
                        <Input
                          placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                          value={ssoConfig.microsoft.client_id}
                          onChange={(e) => setSsoConfig(prev => ({
                            ...prev,
                            microsoft: { ...prev.microsoft, client_id: e.target.value }
                          }))}
                        />
                      </div>
                      <div>
                        <Label>Client Secret</Label>
                        <Input
                          type="password"
                          placeholder="Enter client secret"
                          value={ssoConfig.microsoft.client_secret}
                          onChange={(e) => setSsoConfig(prev => ({
                            ...prev,
                            microsoft: { ...prev.microsoft, client_secret: e.target.value }
                          }))}
                        />
                      </div>
                      <div>
                        <Label>Tenant ID</Label>
                        <Input
                          placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx (or 'common' for multi-tenant)"
                          value={ssoConfig.microsoft.tenant_id}
                          onChange={(e) => setSsoConfig(prev => ({
                            ...prev,
                            microsoft: { ...prev.microsoft, tenant_id: e.target.value }
                          }))}
                        />
                      </div>
                    </div>

                    <Button onClick={() => handleSaveSSO('microsoft')} disabled={saving}>
                      {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                      Save Microsoft SSO
                    </Button>
                  </AccordionContent>
                </AccordionItem>

                {/* Okta SSO */}
                <AccordionItem value="okta" className="border rounded-lg px-4">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3">
                      <OktaIcon />
                      <span className="font-medium">Okta</span>
                      {ssoConfig.okta.enabled ? (
                        <Badge className="bg-green-100 text-green-700 ml-2">Configured</Badge>
                      ) : (
                        <Badge variant="outline" className="ml-2">Not configured</Badge>
                      )}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pt-4 space-y-4">
                    <div className="bg-slate-50 rounded-lg p-4 text-sm">
                      <p className="font-medium mb-2">Setup Steps:</p>
                      <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                        <li>Go to your <a href="https://developer.okta.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Okta Admin Console</a></li>
                        <li>Navigate to Applications → Create App Integration</li>
                        <li>Choose OIDC - OpenID Connect and Web Application</li>
                        <li>Set Sign-in redirect URI: <code className="bg-slate-200 px-1 rounded">{window.location.origin}/api/auth/sso/okta/callback</code></li>
                        <li>Copy Client ID and Client Secret</li>
                      </ol>
                    </div>

                    <div className="flex items-center justify-between">
                      <Label>Enable Okta SSO</Label>
                      <Switch
                        checked={ssoConfig.okta.enabled}
                        onCheckedChange={(checked) => setSsoConfig(prev => ({
                          ...prev,
                          okta: { ...prev.okta, enabled: checked }
                        }))}
                      />
                    </div>

                    <div className="grid gap-4">
                      <div>
                        <Label>Okta Domain</Label>
                        <Input
                          placeholder="your-org.okta.com"
                          value={ssoConfig.okta.domain}
                          onChange={(e) => setSsoConfig(prev => ({
                            ...prev,
                            okta: { ...prev.okta, domain: e.target.value }
                          }))}
                        />
                      </div>
                      <div>
                        <Label>Client ID</Label>
                        <Input
                          placeholder="0oa..."
                          value={ssoConfig.okta.client_id}
                          onChange={(e) => setSsoConfig(prev => ({
                            ...prev,
                            okta: { ...prev.okta, client_id: e.target.value }
                          }))}
                        />
                      </div>
                      <div>
                        <Label>Client Secret</Label>
                        <Input
                          type="password"
                          placeholder="Enter client secret"
                          value={ssoConfig.okta.client_secret}
                          onChange={(e) => setSsoConfig(prev => ({
                            ...prev,
                            okta: { ...prev.okta, client_secret: e.target.value }
                          }))}
                        />
                      </div>
                    </div>

                    <Button onClick={() => handleSaveSSO('okta')} disabled={saving}>
                      {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                      Save Okta SSO
                    </Button>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications Configuration */}
        <TabsContent value="notifications" className="mt-6 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="w-5 h-5" />
                Notification Integrations
              </CardTitle>
              <CardDescription>
                Configure Slack and Microsoft Teams webhooks for real-time security alerts.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Slack */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#4A154B] rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z"/>
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-medium">Slack</h4>
                      <p className="text-sm text-muted-foreground">Send alerts to Slack channels</p>
                    </div>
                  </div>
                  <Switch
                    checked={notificationConfig.slack.enabled}
                    onCheckedChange={(checked) => setNotificationConfig(prev => ({
                      ...prev,
                      slack: { ...prev.slack, enabled: checked }
                    }))}
                  />
                </div>

                {notificationConfig.slack.enabled && (
                  <div className="space-y-4 pt-4 border-t">
                    <div className="bg-slate-50 rounded-lg p-3 text-sm">
                      <p className="font-medium mb-1">How to get Webhook URL:</p>
                      <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                        <li>Go to <a href="https://api.slack.com/apps" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Slack API</a> → Create New App</li>
                        <li>Enable Incoming Webhooks</li>
                        <li>Add New Webhook to Workspace</li>
                        <li>Copy the Webhook URL</li>
                      </ol>
                    </div>
                    <div>
                      <Label>Webhook URL</Label>
                      <Input
                        placeholder="https://hooks.slack.com/services/..."
                        value={notificationConfig.slack.webhook_url}
                        onChange={(e) => setNotificationConfig(prev => ({
                          ...prev,
                          slack: { ...prev.slack, webhook_url: e.target.value }
                        }))}
                      />
                    </div>
                    <div>
                      <Label>Channel (optional)</Label>
                      <Input
                        placeholder="#security-alerts"
                        value={notificationConfig.slack.channel}
                        onChange={(e) => setNotificationConfig(prev => ({
                          ...prev,
                          slack: { ...prev.slack, channel: e.target.value }
                        }))}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={() => handleSaveNotifications('slack')} disabled={saving}>
                        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                        Save
                      </Button>
                      <Button variant="outline" onClick={() => testNotification('slack')}>
                        Send Test
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Microsoft Teams */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#6264a7] rounded-lg flex items-center justify-center">
                      <TeamsIcon />
                    </div>
                    <div>
                      <h4 className="font-medium">Microsoft Teams</h4>
                      <p className="text-sm text-muted-foreground">Send alerts to Teams channels</p>
                    </div>
                  </div>
                  <Switch
                    checked={notificationConfig.teams.enabled}
                    onCheckedChange={(checked) => setNotificationConfig(prev => ({
                      ...prev,
                      teams: { ...prev.teams, enabled: checked }
                    }))}
                  />
                </div>

                {notificationConfig.teams.enabled && (
                  <div className="space-y-4 pt-4 border-t">
                    <div className="bg-slate-50 rounded-lg p-3 text-sm">
                      <p className="font-medium mb-1">How to get Webhook URL:</p>
                      <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                        <li>Open Teams channel → Click ••• → Connectors</li>
                        <li>Find "Incoming Webhook" → Configure</li>
                        <li>Name it "Beskar Security Alerts"</li>
                        <li>Copy the Webhook URL</li>
                      </ol>
                    </div>
                    <div>
                      <Label>Webhook URL</Label>
                      <Input
                        placeholder="https://outlook.office.com/webhook/..."
                        value={notificationConfig.teams.webhook_url}
                        onChange={(e) => setNotificationConfig(prev => ({
                          ...prev,
                          teams: { ...prev.teams, webhook_url: e.target.value }
                        }))}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={() => handleSaveNotifications('teams')} disabled={saving}>
                        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                        Save
                      </Button>
                      <Button variant="outline" onClick={() => testNotification('teams')}>
                        Send Test
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* LLM Configuration */}
        <TabsContent value="llm" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Key className="w-5 h-5" />
                AI/LLM API Keys (BYOK)
              </CardTitle>
              <CardDescription>
                Configure your own AI provider keys for the OSINT AI Assistant feature.
                Visit Settings in the AI Assistant to configure.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
                <Info className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="text-sm">
                    AI configuration is managed per-user in the <strong>OSINT → AI Assistant</strong> page.
                    Each user can bring their own OpenAI, Anthropic, or Google AI key.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default IntegrationsSettings;
